import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  ApiError,
  api,
  type Conversation,
  type ConversationDetail,
  type Defaults,
  type Language,
  type Me,
  type MiniAppRole,
  type TranslationPreferences,
} from "./api";

const LANGUAGES: readonly Language[] = ["th", "en", "ru", "my", "vi"];
const LANGUAGE_META: Record<Language, { flag: string; name: string }> = {
  th: { flag: "🇹🇭", name: "Thai" },
  en: { flag: "🇬🇧", name: "English" },
  ru: { flag: "🇷🇺", name: "Russian" },
  my: { flag: "🇲🇲", name: "Burmese" },
  vi: { flag: "🇻🇳", name: "Vietnamese" },
};

type View =
  | { _tag: "Home" }
  | { _tag: "Profile" }
  | { _tag: "Conversation"; scope: { businessConnectionId: string; chatId: number } };

const homeView: View = { _tag: "Home" };

function viewFromLocation(): View {
  const params = new URLSearchParams(window.location.search);
  const connection = params.get("connection");
  const chat = Number(params.get("chat"));
  return connection && Number.isInteger(chat)
    ? { _tag: "Conversation", scope: { businessConnectionId: connection, chatId: chat } }
    : homeView;
}

function updateLocation(view: View): void {
  const url = new URL(window.location.href);
  url.search = "";
  if (view._tag === "Conversation") {
    url.searchParams.set("connection", view.scope.businessConnectionId);
    url.searchParams.set("chat", String(view.scope.chatId));
  }
  window.history.pushState({}, "", url);
}

function peerName(conversation: Conversation): string {
  return conversation.peer.displayName || `Chat ${conversation.chatId}`;
}

function formatDirection(sources: readonly Language[], target: Language): string {
  return `${sources.map((language) => LANGUAGE_META[language].flag).join(" ")} → ${LANGUAGE_META[target].flag}`;
}

function formatError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return "These settings changed elsewhere. Reload and try again.";
    if (error.status === 401) {
      const detail = [error.payload.reason, error.payload.variant]
        .filter((value): value is string => typeof value === "string")
        .join(" · ");
      return `Telegram could not verify this session. Close and reopen the Mini App.${detail ? ` (${detail})` : ""}`;
    }
    if (error.status === 403) return "This Telegram account does not own this conversation.";
  }
  return "Something went wrong. Please try again.";
}

function Shell(props: {
  me: Me;
  view: View;
  onBack: () => void;
  onOpenProfile?: () => void;
  children: ReactNode;
}) {
  const inSubpage = props.view._tag !== "Home";
  return (
    <main className="app-shell">
      <header className="masthead">
        <div className="masthead__rule" aria-hidden="true" />
        <div className="masthead__row">
          {inSubpage ? (
            <button className="icon-button" type="button" onClick={props.onBack} aria-label="Back">
              ←
            </button>
          ) : (
            <span className="edition-mark" aria-hidden="true">ꙮ</span>
          )}
          <div className="masthead__titles">
            <p>Between your languages</p>
            <h1>Kapooncat</h1>
          </div>
          {props.view._tag === "Home" && props.onOpenProfile ? (
            <button className="text-button" type="button" onClick={props.onOpenProfile}>
              Profile
            </button>
          ) : (
            <span className="owner-chip">{props.me.firstName?.slice(0, 1) || "O"}</span>
          )}
        </div>
      </header>
      {props.children}
    </main>
  );
}

function DirectionStamp(props: {
  title: string;
  sources: readonly Language[];
  target: Language;
}) {
  return (
    <div className="direction-stamp">
      <span>{props.title}</span>
      <strong>{formatDirection(props.sources, props.target)}</strong>
    </div>
  );
}

function formatUsd(microusd: number): string {
  const dollars = Math.floor(Math.abs(microusd) / 1_000_000);
  const cents = Math.floor((Math.abs(microusd) % 1_000_000) / 10_000);
  return `$${dollars}.${String(cents).padStart(2, "0")}`;
}

function Dashboard(props: {
  conversations: Conversation[];
  me: Me;
  onOpen: (conversation: Conversation) => void;
}) {
  return (
    <section className="page page--dashboard">
      {props.me.balance && (
        <p className="balance-strip">
          {formatUsd(props.me.balance.spentMicrousd)} of {formatUsd(props.me.balance.allocatedMicrousd)} used for translations so far.
        </p>
      )}
      <div className="page-intro reveal">
        <p className="eyebrow">Your conversations</p>
        <h2>The people you talk to,<br /><em>in both languages.</em></h2>
      </div>
      {props.conversations.length === 0 ? (
        <div className="empty-state reveal reveal--2">
          <span aria-hidden="true">◎</span>
          <h3>No conversations yet</h3>
          <p>Connect the bot as your Telegram Business chat manager, then open this Mini App from the bot chat.</p>
        </div>
      ) : (
        <div className="conversation-list">
          {props.conversations.map((conversation, index) => (
            <button
              className="conversation-card reveal"
              style={{ animationDelay: `${100 + index * 65}ms` }}
              type="button"
              key={`${conversation.businessConnectionId}:${conversation.chatId}`}
              onClick={() => props.onOpen(conversation)}
            >
              <span className={`status-dot ${conversation.active ? "is-active" : ""}`} />
              <div className="conversation-card__identity">
                <p>{conversation.active ? "Translating" : "Paused"}</p>
                <h3>{peerName(conversation)}</h3>
                {conversation.peer.username && <span>@{conversation.peer.username}</span>}
              </div>
              <div className="conversation-card__routes">
                <DirectionStamp
                  title={props.me.role === "participant" ? "They send" : "You send"}
                  sources={conversation.ownerSourceLanguages}
                  target={conversation.ownerTargetLanguage}
                />
                <DirectionStamp
                  title={props.me.role === "participant" ? "You send" : "They send"}
                  sources={conversation.partnerSourceLanguages}
                  target={conversation.partnerTargetLanguage}
                />
              </div>
              <div className="conversation-card__footer">
                <span>{conversation.historyWindow} context turns</span>
                <b aria-hidden="true">↗</b>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function InheritToggle(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="inherit-toggle">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(event) => props.onChange(event.target.checked)}
      />
      <span>{props.children}</span>
    </label>
  );
}

function Section(props: {
  number: string;
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section className="form-section">
      <div className="form-section__heading">
        <span>{props.number}</span>
        <div>
          <h3>{props.title}</h3>
          {props.note && <p>{props.note}</p>}
        </div>
      </div>
      <div className="form-section__body">{props.children}</div>
    </section>
  );
}

function LanguageRouteEditor(props: {
  label: string;
  detail: string;
  sources: Language[];
  target: Language;
  onSources: (sources: Language[]) => void;
  onTarget: (target: Language) => void;
  targetEditable?: boolean;
}) {
  const toggle = (language: Language) => {
    if (props.sources.includes(language)) {
      if (props.sources.length > 1) {
        props.onSources(props.sources.filter((item) => item !== language));
      }
    } else {
      props.onSources([...props.sources, language]);
    }
  };
  return (
    <div className="route-editor">
      <div className="route-editor__title">
        <div>
          <h4>{props.label}</h4>
          <p>{props.detail}</p>
        </div>
        <span>{formatDirection(props.sources, props.target)}</span>
      </div>
      <label className="field-label">Translate from</label>
      <div className="language-grid">
        {LANGUAGES.map((language) => (
          <button
            key={language}
            type="button"
            className={props.sources.includes(language) ? "is-selected" : ""}
            aria-pressed={props.sources.includes(language)}
            onClick={() => toggle(language)}
          >
            <span>{LANGUAGE_META[language].flag}</span>
            {LANGUAGE_META[language].name}
          </button>
        ))}
      </div>
      <label className="field-label" htmlFor={`${props.label}-target`}>Translate into</label>
      <select
        id={`${props.label}-target`}
        value={props.target}
        disabled={props.targetEditable === false}
        onChange={(event) => props.onTarget(event.target.value as Language)}
      >
        {LANGUAGES.map((language) => (
          <option key={language} value={language}>
            {LANGUAGE_META[language].flag} {LANGUAGE_META[language].name}
          </option>
        ))}
      </select>
    </div>
  );
}

function Segmented<T extends string>(props: {
  label: string;
  value: T;
  options: readonly T[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented-field">
      <label>{props.label}</label>
      <div className="segmented">
        {props.options.map((option) => (
          <button
            key={option}
            type="button"
            className={props.value === option ? "is-selected" : ""}
            aria-pressed={props.value === option}
            onClick={() => props.onChange(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function StyleEditor(props: {
  value: TranslationPreferences;
  onChange: (value: TranslationPreferences) => void;
}) {
  return (
    <div className="style-editor">
      <Segmented
        label="Tone"
        value={props.value.tone}
        options={["preserve", "warm", "neutral"]}
        onChange={(tone) => props.onChange({ ...props.value, tone })}
      />
      <Segmented
        label="Formality"
        value={props.value.formality}
        options={["preserve", "casual", "polite"]}
        onChange={(formality) => props.onChange({ ...props.value, formality })}
      />
      <Segmented
        label="Literalness"
        value={props.value.literalness}
        options={["balanced", "natural", "literal"]}
        onChange={(literalness) => props.onChange({ ...props.value, literalness })}
      />
    </div>
  );
}

function KeyTermsEditor(props: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const term = draft.trim();
    if (!term || props.value.includes(term) || props.value.length >= 100) return;
    props.onChange([...props.value, term]);
    setDraft("");
  };
  return (
    <div className="keyterms">
      <div className="keyterms__list">
        {props.value.length === 0 && <span className="inherit-note">No key terms set</span>}
        {props.value.map((term) => (
          <button
            type="button"
            key={term}
            onClick={() => props.onChange(props.value.filter((item) => item !== term))}
            title={`Remove ${term}`}
          >
            {term} <span aria-hidden="true">×</span>
          </button>
        ))}
      </div>
      <div className="keyterms__entry">
        <input
          value={draft}
          maxLength={80}
          placeholder="Name, place, or product term"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <button type="button" onClick={add}>Add</button>
      </div>
    </div>
  );
}

function ReflectPane(props: {
  conversation: Conversation;
}) {
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState<Array<{ role: "you" | "kapooncat"; text: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [notes, setNotes] = useState<string[]>([]);
  const [noteBusy, setNoteBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void api.notes(props.conversation).then((result) => {
      if (!cancelled) setNotes(result.lines);
    }).catch(() => {
      if (!cancelled) setNotes([]);
    });
    return () => {
      cancelled = true;
    };
  }, [props.conversation.businessConnectionId, props.conversation.chatId]);
  const keep = async () => {
    const text = noteDraft.trim();
    if (!text || noteBusy) return;
    setNoteBusy(true);
    try {
      await api.keepNote(props.conversation, text);
      setNoteDraft("");
      setNotes((await api.notes(props.conversation)).lines);
    } catch (error) {
      setNotes((current) => [...current, formatError(error)]);
    } finally {
      setNoteBusy(false);
    }
  };
  const send = async () => {
    const text = question.trim();
    if (!text || busy) return;
    setQuestion("");
    setTurns((current) => [...current, { role: "you", text }]);
    setBusy(true);
    try {
      const result = await api.reflect(
        props.conversation,
        text,
        turns.map((turn) => ({
          role: turn.role === "you" ? "asker" as const : "bot" as const,
          text: turn.text,
        })),
      );
      setTurns((current) => [...current, { role: "kapooncat", text: result.answer }]);
    } catch (error) {
      setTurns((current) => [...current, { role: "kapooncat", text: formatError(error) }]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="reflect-pane">
      <p className="inherit-note">
        Ask about the recent conversation — what a phrase means, what got lost,
        how to say something. A note you keep is yours, not the other
        person's; /bridge_forget erases it.
      </p>
      <ol className="reflect-turns">
        {turns.map((turn, index) => (
          <li key={index} className={`reflect-turn reflect-turn--${turn.role}`}>
            <strong>{turn.role === "you" ? "You" : "Kapooncat"}</strong>
            <p>{turn.text}</p>
          </li>
        ))}
      </ol>
      <div className="reflect-compose">
        <input
          value={question}
          placeholder="Ask about a phrase or a translation"
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void send();
            }
          }}
        />
        <button type="button" disabled={busy} onClick={() => void send()}>
          {busy ? "…" : "Ask"}
        </button>
      </div>
      {notes.length > 0 && (
        <ol className="note-lines">
          {notes.map((line, index) => (
            <li key={`${index}:${line}`}>{line}</li>
          ))}
        </ol>
      )}
      <div className="reflect-compose">
        <input
          value={noteDraft}
          maxLength={280}
          placeholder="Keep a line — a misread of this chat's language"
          onChange={(event) => setNoteDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void keep();
            }
          }}
        />
        <button type="button" disabled={noteBusy} onClick={() => void keep()}>
          {noteBusy ? "…" : "Keep"}
        </button>
      </div>
    </div>
  );
}

type ConversationDraft = {
  active: boolean;
  ownerSources: Language[];
  ownerTarget: Language;
  partnerSources: Language[];
  partnerTarget: Language;
  historyWindow: number;
  debounceMs: number;
  keyterms: string[];
  overrideStyle: boolean;
  preferences: TranslationPreferences;
  overrideInstructions: boolean;
  instructions: string;
  overrideRetention: boolean;
  retentionDays: number;
};

function draftFromConversation(conversation: Conversation): ConversationDraft {
  return {
    active: conversation.active,
    ownerSources: [...conversation.ownerSourceLanguages],
    ownerTarget: conversation.ownerTargetLanguage,
    partnerSources: [...conversation.partnerSourceLanguages],
    partnerTarget: conversation.partnerTargetLanguage,
    historyWindow: conversation.historyWindow,
    debounceMs: conversation.debounceMs,
    keyterms: [...(conversation.keyterms ?? [])],
    overrideStyle: Object.keys(conversation.translationPreferences.override).length > 0,
    preferences: { ...conversation.translationPreferences.effective },
    overrideInstructions: conversation.translationInstructions?.override != null,
    instructions: conversation.translationInstructions?.override ?? "",
    overrideRetention: conversation.retentionDays.override !== null,
    retentionDays:
      conversation.retentionDays.override
      ?? conversation.retentionDays.effective
      ?? 30,
  };
}

function ConversationEditor(props: {
  detail: ConversationDetail;
  role: MiniAppRole;
  saving: boolean;
  notice: string | null;
  onSave: (draft: ConversationDraft) => Promise<void>;
}) {
  const conversation = props.detail.conversation;
  const participant = props.role === "participant";
  const [draft, setDraft] = useState(() => draftFromConversation(conversation));
  useEffect(() => setDraft(draftFromConversation(conversation)), [conversation]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void props.onSave(draft);
  };
  return (
    <form className="page editor" onSubmit={submit}>
      <div className="editor-hero reveal">
        <div>
          <p className="eyebrow">Conversation</p>
          <h2>{peerName(conversation)}</h2>
          <p>Help define the companion's language, then ask Kapooncat about the talk.</p>
        </div>
        {participant ? (
          draft.active ? (
            <button
              type="button"
              className="text-button"
              onClick={() => setDraft({ ...draft, active: false })}
            >
              Turn Kapooncat off
            </button>
          ) : (
            <span className="inherit-note">Paused</span>
          )
        ) : (
          <label className="switch">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
            />
            <span />
            {draft.active ? "Active" : "Paused"}
          </label>
        )}
      </div>

      <Section number="01" title="Language routes" note="Help define the companion's language">
        {!participant && (
          <LanguageRouteEditor
            label="When you write"
            detail="Messages sent from your Business account"
            sources={draft.ownerSources}
            target={draft.ownerTarget}
            onSources={(ownerSources) => setDraft({ ...draft, ownerSources })}
            onTarget={(ownerTarget) => setDraft({ ...draft, ownerTarget })}
          />
        )}
        <LanguageRouteEditor
          label={participant ? "Languages you write" : "When they write"}
          detail={participant ? "The chief reads these as your source languages" : "Messages arriving from this person"}
          sources={draft.partnerSources}
          target={draft.partnerTarget}
          onSources={(partnerSources) => setDraft({ ...draft, partnerSources })}
          onTarget={(partnerTarget) => setDraft({ ...draft, partnerTarget })}
          targetEditable={!participant}
        />
      </Section>

      <Section number="02" title="Ask Kapooncat">
        <ReflectPane conversation={conversation} />
      </Section>

      {!participant && (
      <details className="advanced-settings">
        <summary>Advanced</summary>
        <div className="advanced-settings__body">
        <KeyTermsEditor
          value={draft.keyterms}
          onChange={(keyterms) => setDraft({ ...draft, keyterms })}
        />
        <InheritToggle
          checked={draft.overrideStyle}
          onChange={(overrideStyle) => setDraft({ ...draft, overrideStyle })}
        >
          Override global style for this conversation
        </InheritToggle>
        <div className={!draft.overrideStyle ? "is-inherited" : ""}>
          <StyleEditor
            value={draft.preferences}
            onChange={(preferences) => setDraft({ ...draft, preferences })}
          />
        </div>
        <InheritToggle
          checked={draft.overrideInstructions}
          onChange={(overrideInstructions) => setDraft({ ...draft, overrideInstructions })}
        >
          Add conversation-specific guidance
        </InheritToggle>
        {conversation.translationInstructions?.inherited && (
          <p className="inherited-copy">
            <b>Global:</b> {conversation.translationInstructions.inherited}
          </p>
        )}
        <textarea
          rows={5}
          maxLength={2_000}
          disabled={!draft.overrideInstructions}
          value={draft.instructions}
          placeholder="For example: Keep names in Latin script. Prefer short, warm sentences."
          onChange={(event) => setDraft({ ...draft, instructions: event.target.value })}
        />
        <p className="character-count">{draft.instructions.length} / 2,000</p>
        <div className="range-field">
          <div>
            <label htmlFor="history-window">Context depth</label>
            <output>{draft.historyWindow} turns</output>
          </div>
          <input
            id="history-window"
            type="range"
            min="0"
            max="50"
            value={draft.historyWindow}
            onChange={(event) => setDraft({
              ...draft,
              historyWindow: Number(event.target.value),
            })}
          />
        </div>
          <InheritToggle
            checked={draft.overrideRetention}
            onChange={(overrideRetention) => setDraft({ ...draft, overrideRetention })}
          >
            Override global retention
          </InheritToggle>
          <label>
            Retain text history for
            <input
              type="number"
              min="1"
              max="3650"
              disabled={!draft.overrideRetention}
              value={draft.retentionDays}
              onChange={(event) => setDraft({
                ...draft,
                retentionDays: Number(event.target.value),
              })}
            />
            days
          </label>
          <label>
            Batch nearby messages for
            <input
              type="number"
              min="0"
              max="60000"
              step="1000"
              value={draft.debounceMs}
              onChange={(event) => setDraft({
                ...draft,
                debounceMs: Number(event.target.value),
              })}
            />
            ms
          </label>
        </div>
      </details>
      )}

      <div className="save-dock">
        <p aria-live="polite">{props.notice || "Changes apply to the next translation job."}</p>
        <button type="submit" disabled={props.saving}>
          {props.saving ? "Saving…" : "Save conversation"}
        </button>
      </div>
    </form>
  );
}

type DefaultsDraft = Omit<Defaults, "configVersion"> & {
  keepForever: boolean;
  retentionDaysValue: number;
};

function DefaultsEditor(props: {
  defaults: Defaults;
  balance: Me["balance"];
  saving: boolean;
  notice: string | null;
  onSave: (draft: DefaultsDraft) => Promise<void>;
}) {
  const fromDefaults = (defaults: Defaults): DefaultsDraft => ({
    translationPreferences: { ...defaults.translationPreferences },
    translationInstructions: defaults.translationInstructions,
    understoodLanguages: [...defaults.understoodLanguages],
    retentionDays: defaults.retentionDays,
    keepForever: defaults.retentionDays === null,
    retentionDaysValue: defaults.retentionDays ?? 30,
  });
  const [draft, setDraft] = useState(() => fromDefaults(props.defaults));
  useEffect(() => setDraft(fromDefaults(props.defaults)), [props.defaults]);
  return (
    <form
      className="page editor"
      onSubmit={(event) => {
        event.preventDefault();
        void props.onSave(draft);
      }}
    >
      <div className="editor-hero reveal">
        <div>
          <p className="eyebrow">Profile</p>
          <h2>Profile</h2>
          <p>Style, retention, languages you already understand, and the trial balance.</p>
        </div>
        <span className="folio">v{props.defaults.configVersion}</span>
      </div>
      <Section number="01" title="House translation style">
        <StyleEditor
          value={draft.translationPreferences}
          onChange={(translationPreferences) => setDraft({ ...draft, translationPreferences })}
        />
        <label className="field-label" htmlFor="global-instructions">Guarded translation guidance</label>
        <textarea
          id="global-instructions"
          rows={6}
          maxLength={2_000}
          value={draft.translationInstructions}
          placeholder="For example: Keep names in Latin script. Prefer concise, warm phrasing."
          onChange={(event) => setDraft({
            ...draft,
            translationInstructions: event.target.value,
          })}
        />
        <p className="character-count">{draft.translationInstructions.length} / 2,000</p>
      </Section>
      <Section number="02" title="Languages I understand">
        <p className="inherit-note">Partner messages in these languages are stored, not translated.</p>
        <div className="language-grid">
          {LANGUAGES.map((language) => {
            const selected = draft.understoodLanguages.includes(language);
            return (
              <button
                key={language}
                type="button"
                className={selected ? "is-selected" : ""}
                aria-pressed={selected}
                onClick={() => setDraft({
                  ...draft,
                  understoodLanguages: selected
                    ? draft.understoodLanguages.filter((item) => item !== language)
                    : [...draft.understoodLanguages, language],
                })}
              >
                <span>{LANGUAGE_META[language].flag}</span>
                {LANGUAGE_META[language].name}
              </button>
            );
          })}
        </div>
      </Section>
      <Section number="03" title="Retention">
        <InheritToggle
          checked={draft.keepForever}
          onChange={(keepForever) => setDraft({ ...draft, keepForever })}
        >
          Keep text history until I change this setting
        </InheritToggle>
        {!draft.keepForever && (
          <label className="inline-number">
            Retention
            <input
              type="number"
              min="1"
              max="3650"
              value={draft.retentionDaysValue}
              onChange={(event) => setDraft({
                ...draft,
                retentionDaysValue: Number(event.target.value),
              })}
            />
            days
          </label>
        )}
      </Section>
      {props.balance && (
        <Section number="04" title="Balance">
          <p>
            {formatUsd(props.balance.spentMicrousd)} of {formatUsd(props.balance.allocatedMicrousd)} used.
          </p>
          <p className="inherit-note">Translations use real model calls, so they draw from this balance.</p>
        </Section>
      )}
      <div className="save-dock">
        <p aria-live="polite">{props.notice || "Conversation overrides remain unchanged."}</p>
        <button type="submit" disabled={props.saving}>
          {props.saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </form>
  );
}

function Loading() {
  return (
    <main className="state-page">
      <span className="loading-mark">CC</span>
      <p>Waking Kapooncat…</p>
    </main>
  );
}

function Unavailable(props: { error?: string }) {
  return (
    <main className="state-page state-page--error">
      <span>↗</span>
      <h1>Open this inside Telegram</h1>
      <p>{props.error || "The Mini App needs a signed Telegram session to show private settings."}</p>
    </main>
  );
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [defaults, setDefaults] = useState<Defaults | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [view, setView] = useState<View>(() => viewFromLocation());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);

  const telegram = window.Telegram?.WebApp;
  useEffect(() => {
    telegram?.ready();
    telegram?.expand();
    if (telegram?.colorScheme === "dark") {
      document.documentElement.dataset.theme = "dark";
      telegram.setHeaderColor("#171915");
      telegram.setBackgroundColor("#171915");
    } else {
      telegram?.setHeaderColor("#f1eadc");
      telegram?.setBackgroundColor("#f1eadc");
    }
  }, [telegram]);

  const loadDetail = async (scope: {
    businessConnectionId: string;
    chatId: number;
  }) => {
    const loaded = await api.conversation(scope);
    setDetail(loaded);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!api.hasInitData()) {
        setFatal("The Mini App needs a signed Telegram session to show private settings.");
        setLoading(false);
        return;
      }
      try {
        const [loadedMe, loadedConversations] = await Promise.all([
          api.me(),
          api.conversations(),
        ]);
        const loadedDefaults = loadedMe.role === "participant" || loadedMe.role === "stranger"
          ? null
          : await api.defaults();
        if (cancelled) return;
        setMe(loadedMe);
        setDefaults(loadedDefaults);
        setConversations(loadedConversations);
        const fromToken = loadedMe.startScope
          ? { _tag: "Conversation" as const, scope: loadedMe.startScope }
          : viewFromLocation();
        if (fromToken._tag === "Conversation") {
          setView(fromToken);
          updateLocation(fromToken);
          await loadDetail(fromToken.scope);
        }
      } catch (error) {
        if (!cancelled) setFatal(formatError(error));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const pop = () => {
      const next = viewFromLocation();
      setView(next);
      setNotice(null);
      if (next._tag === "Conversation") void loadDetail(next.scope);
      else setDetail(null);
    };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);

  const open = async (conversation: Conversation) => {
    const next: View = {
      _tag: "Conversation",
      scope: {
        businessConnectionId: conversation.businessConnectionId,
        chatId: conversation.chatId,
      },
    };
    setView(next);
    updateLocation(next);
    setNotice(null);
    setDetail(null);
    try {
      await loadDetail(next.scope);
    } catch (error) {
      setNotice(formatError(error));
    }
  };

  const navigate = (next: View) => {
    setView(next);
    updateLocation(next);
    setNotice(null);
    if (next._tag !== "Conversation") setDetail(null);
  };

  const saveConversation = async (draft: ConversationDraft) => {
    if (!detail) return;
    setSaving(true);
    setNotice(null);
    try {
      const updated = await api.patchConversation(detail.conversation, me?.role === "participant"
        ? {
            configVersion: detail.conversation.configVersion,
            partnerSourceLanguages: draft.partnerSources,
            ...(draft.active ? {} : { active: false }),
          }
        : {
            configVersion: detail.conversation.configVersion,
            active: draft.active,
            ownerSourceLanguages: draft.ownerSources,
            ownerTargetLanguage: draft.ownerTarget,
            partnerSourceLanguages: draft.partnerSources,
            partnerTargetLanguage: draft.partnerTarget,
            historyWindow: draft.historyWindow,
            debounceMs: draft.debounceMs,
            keyterms: draft.keyterms,
            translationPreferences: draft.overrideStyle ? draft.preferences : null,
            translationInstructions: draft.overrideInstructions ? draft.instructions : null,
            retentionDays: draft.overrideRetention ? draft.retentionDays : null,
          });
      setDetail(updated);
      setConversations((current) => current.map((conversation) =>
        conversation.businessConnectionId === updated.conversation.businessConnectionId
        && conversation.chatId === updated.conversation.chatId
          ? updated.conversation
          : conversation
      ));
      setNotice("Saved. The next translation will use this configuration.");
      telegram?.HapticFeedback?.notificationOccurred("success");
    } catch (error) {
      setNotice(formatError(error));
      telegram?.HapticFeedback?.notificationOccurred("error");
      if (error instanceof ApiError && error.status === 409) {
        await loadDetail(detail.conversation);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDefaults = async (draft: DefaultsDraft) => {
    if (!defaults) return;
    setSaving(true);
    setNotice(null);
    try {
      const updated = await api.patchDefaults({
        configVersion: defaults.configVersion,
        translationPreferences: draft.translationPreferences,
        translationInstructions: draft.translationInstructions,
        understoodLanguages: draft.understoodLanguages,
        retentionDays: draft.keepForever ? null : draft.retentionDaysValue,
      });
      setDefaults(updated);
      setNotice("Global defaults saved.");
      telegram?.HapticFeedback?.notificationOccurred("success");
    } catch (error) {
      setNotice(formatError(error));
      telegram?.HapticFeedback?.notificationOccurred("error");
      if (error instanceof ApiError && error.status === 409) {
        setDefaults(await api.defaults());
      }
    } finally {
      setSaving(false);
    }
  };

  const title = useMemo(() => {
    if (view._tag === "Profile") return "Profile";
    if (view._tag === "Conversation" && detail) return peerName(detail.conversation);
    return "Conversations";
  }, [detail, view]);
  useEffect(() => {
    document.title = `${title} · Kapooncat`;
  }, [title]);

  if (loading) return <Loading />;
  if (fatal || !me) return <Unavailable error={fatal ?? undefined} />;
  const operator = me.role === "creator" || me.role === "chief";

  return (
    <Shell
      me={me}
      view={view}
      onBack={() => navigate(homeView)}
      onOpenProfile={operator ? () => navigate({ _tag: "Profile" }) : undefined}
    >
      {view._tag === "Home" && (
        <Dashboard me={me} conversations={conversations} onOpen={(conversation) => void open(conversation)} />
      )}
      {view._tag === "Profile" && defaults && (
        <DefaultsEditor
          defaults={defaults}
          balance={me.balance}
          saving={saving}
          notice={notice}
          onSave={saveDefaults}
        />
      )}
      {view._tag === "Conversation" && (
        detail ? (
          <ConversationEditor
            detail={detail}
            role={me.role}
            saving={saving}
            notice={notice}
            onSave={saveConversation}
          />
        ) : (
          <Loading />
        )
      )}
    </Shell>
  );
}
